import './style.scss';
import gsap from 'gsap';

/**
 * Creates and manages the GSAP timeline for the introduction animation.
 *
 * @param {HTMLElement} instance - The DOM element containing the animation elements.
 * @param {string} selector - The base class selector for identifying animation elements.
 * @param {Object} view - An object representing the current viewport size (e.g., `xs`, `md`, `lg`).
 * @returns {gsap.core.Timeline} - The GSAP timeline instance controlling the animation.
 */
const createAnimation = (instance, selector, view) => {
  let logoWidth, headerPad;
  const timeline = gsap.timeline({ paused: true });
  const bg = instance.querySelector(`.${selector}-bg`);
  const logo = instance.querySelector(`.${selector}-logo`);
  const animationSettings = { ease: 'power1.inOut' };

  if (view['xs']) {
    logoWidth = 141;
    headerPad = 30;
  }
  if (view['sm']) {
    logoWidth = 141;
    headerPad = 30;
  }
  if (view['md']) {
    logoWidth = 208;
    headerPad = 40;
  }
  if (view['lg']) {
    logoWidth = 208;
    headerPad = 40;
  }
  if (view['xl']) {
    logoWidth = 208;
    headerPad = 40;
  }
  if (view['xxl']) {
    logoWidth = 256;
    headerPad = 40;
  }
  if (view['1080p']) {
    logoWidth = 237;
    headerPad = 40;
  }
  if (view['1440p']) {
    logoWidth = 316;
    headerPad = 40;
  }
  if (view['4k']) {
    logoWidth = 475;
    headerPad = 40;
  }

  // prettier-ignore
  {
    timeline.add('start');
    timeline.set('.nav_primary-logo', { autoAlpha: 0 });
    timeline.to(bg, { ...animationSettings, duration: 1, delay: 1.25, autoAlpha: 0 }, 'start');
    timeline.to(logo, { ...animationSettings, duration: 1, delay: 1.25, top: headerPad, width: logoWidth, translate: '-50% 0' }, 'start')
      .set('.nav_primary-logo', { autoAlpha: 1 })
      .to(instance, { ...animationSettings, duration: 0.25, autoAlpha: 0 });
  }

  return timeline;
};

/**
 * Initializes the animation for a specific instance of the introduction animation component.
 *
 * @param {HTMLElement} instance - The DOM element containing the introduction animation.
 * @param {string} selector - The base class selector for identifying the animation elements.
 *
 * Includes a 'skip' link for users to bypass the animation and update the URL accordingly.
 */
const initAnimation = (instance, selector) => {
  if (!instance) return;

  const view = {
    'xs': window.matchMedia('(max-width: 575px)').matches,
    'sm': window.matchMedia('(min-width: 576px) and (max-width: 767px)')
      .matches,
    'md': window.matchMedia('(min-width: 768px) and (max-width: 991px)')
      .matches,
    'lg': window.matchMedia('(min-width: 992px) and (max-width: 1199px)')
      .matches,
    'xl': window.matchMedia('(min-width: 1200px) and (max-width: 1399px)')
      .matches,
    'xxl': window.matchMedia('(min-width: 1400px) and (max-width: 1919px)')
      .matches,
    '1080p': window.matchMedia('(min-width: 1920px) and (max-width: 2559px)')
      .matches,
    '1440p': window.matchMedia('(min-width: 2560px) and (max-width: 3839px)')
      .matches,
    '4k': window.matchMedia('(min-width: 3840px)').matches,
  };
  const mainTimeline = createAnimation(instance, selector, view);
  const skipLink = instance.querySelector(`.${selector}-link`);

  mainTimeline.play();

  skipLink.addEventListener('click', event => {
    event.preventDefault();
    mainTimeline.progress(1).kill();

    const url = new URL(window.location.href);
    url.searchParams.set('intro', 'skipped');
    history.pushState(null, '', url.toString());
  });
};

/**
 * Initializes the introduction animation for all matching elements on the page.
 */
document.addEventListener('DOMContentLoaded', () => {
  const selector = 'introduction_animation';
  const elements = document.querySelectorAll(`.${selector}`);
  elements.forEach(instance => initAnimation(instance, selector));
});
